import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { leadsApi, type Lead } from "@/api/leads";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

function leadsListPathForRole(role: string | undefined): string {
  if (role === "SUPER_ADMIN") return "/super-admin/leads";
  if (role === "SALES_PERSON" || role === "SALES_MANAGER") return "/sales-person/leads";
  return "/hospital-admin/leads";
}

export default function LeadCallNowAlertDialog() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const skipMarkOnCloseRef = useRef(false);
  /** Hide pending rows immediately after dismiss so refetch cannot reopen the modal before ack finishes. */
  const locallyAckedLeadIdsRef = useRef(new Set<string>());
  const [open, setOpen] = useState(false);
  const [displayLeads, setDisplayLeads] = useState<Lead[]>([]);

  const { data } = useQuery({
    queryKey: ["lead-call-now-pending"],
    queryFn: async () => {
      const res = await leadsApi.getPendingCallNowLeads();
      return res.data.data as Lead[];
    },
    refetchInterval: 20_000,
    enabled: user?.role === "SALES_PERSON" || user?.role === "SALES_MANAGER",
  });

  const ackMut = useMutation({
    mutationFn: (leadIds: string[]) =>
      Promise.all(leadIds.map((id) => leadsApi.acknowledgeCallNow(id))),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["lead-call-now-pending"] });
    },
  });

  const visiblePending = (data ?? []).filter((l) => !locallyAckedLeadIdsRef.current.has(l.id));

  useEffect(() => {
    const list = visiblePending;
    if (list.length === 0) {
      locallyAckedLeadIdsRef.current.clear();
      if (open) {
        skipMarkOnCloseRef.current = true;
        setOpen(false);
        setDisplayLeads([]);
      }
      return;
    }
    if (open) {
      setDisplayLeads(list);
      return;
    }
    setDisplayLeads(list);
    setOpen(true);
  }, [data, open]);

  const dismissAndClose = () => {
    const ids = displayLeads.map((l) => l.id);
    for (const id of ids) locallyAckedLeadIdsRef.current.add(id);
    if (ids.length) ackMut.mutate(ids);
    skipMarkOnCloseRef.current = true;
    setOpen(false);
    setDisplayLeads([]);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          if (!skipMarkOnCloseRef.current) {
            const ids = displayLeads.map((l) => l.id);
            for (const id of ids) locallyAckedLeadIdsRef.current.add(id);
            if (ids.length) ackMut.mutate(ids);
          }
          skipMarkOnCloseRef.current = false;
          setOpen(false);
          setDisplayLeads([]);
        } else {
          setOpen(true);
        }
      }}
    >
      <DialogContent className="sm:max-w-md border-red-300">
        <DialogHeader>
          <DialogTitle className="text-red-800">Customer wants a call now</DialogTitle>
          <p className="text-sm text-muted-foreground pt-1">
            The customer tapped <strong>Call Now Please</strong> on the missed follow-up WhatsApp.
            Call immediately.
          </p>
        </DialogHeader>
        <ul className="max-h-60 overflow-y-auto divide-y rounded-md border border-red-200 bg-red-50/40">
          {displayLeads.map((l) => (
            <li key={l.id} className="px-3 py-2 text-sm">
              <div className="font-semibold text-red-950">{l.patientName}</div>
              <div className="text-xs text-red-800/80">{l.phone}</div>
              {l.assignedTo?.name ? (
                <div className="text-xs text-muted-foreground">Owner: {l.assignedTo.name}</div>
              ) : null}
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-xs text-red-700"
                onClick={() => {
                  dismissAndClose();
                  const base = leadsListPathForRole(user?.role);
                  navigate(`${base}?openLeadId=${encodeURIComponent(l.id)}`);
                }}
              >
                Open lead
              </Button>
            </li>
          ))}
        </ul>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={dismissAndClose}>
            Acknowledge
          </Button>
          <Button type="button" className="bg-red-700 hover:bg-red-800" onClick={dismissAndClose}>
            Open Leads
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
